import React, { useState } from 'react';
import { useTeamCoaches } from '@/hooks/useTeamCoaches';
import { useClubCoachData } from '@/hooks/useClubCoachData';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, UserCheck } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';

interface TeamCoachManagementProps {
  teamId: string;
  clubId: string;
}

export const TeamCoachManagement = ({ teamId, clubId }: TeamCoachManagementProps) => {
  const { coaches, loading, addCoach, removeCoach, updateCoachRole } = useTeamCoaches(teamId);
  const { clubCoaches, coachesLoaded } = useClubCoachData(clubId);
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [selectedRole, setSelectedRole] = useState('assistant_coach');
  const [isAdding, setIsAdding] = useState(false);

  const availableCoaches = clubCoaches.filter(
    clubCoach => !coaches.some(teamCoach => teamCoach.coach_id === clubCoach.id)
  );

  const handleAddCoach = async () => {
    if (!selectedCoachId) {
      return;
    }
    
    try {
      setIsAdding(true);
      const success = await addCoach(selectedCoachId, selectedRole);
      
      if (success) {
        setSelectedCoachId('');
        setSelectedRole('assistant_coach');
      }
    } catch (error) {
      console.error('Error in handleAddCoach:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'head_coach':
        return 'bg-blue-100 text-blue-800';
      case 'assistant_coach':
        return 'bg-green-100 text-green-800';
      case 'team_parent':
        return 'bg-purple-100 text-purple-800';
      case 'board_member':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'head_coach':
        return 'Head Coach';
      case 'assistant_coach':
        return 'Assistant Coach';
      case 'team_parent':
        return 'Team Parent';
      case 'board_member':
        return 'Board Member';
      default:
        return role;
    }
  };

  if (loading || !coachesLoaded) {
    return <div>Loading coaches...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Add Coach Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Coach
          </CardTitle>
          <CardDescription>
            Assign coaches from your club to this team. 
            {clubCoaches.length === 0 && (
              <span className="text-orange-600 font-medium">
                No club coaches found - you may need to add coaches to the club first.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium">Select Coach</label>
              <Select value={selectedCoachId} onValueChange={setSelectedCoachId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a coach..." />
                </SelectTrigger>
                <SelectContent>
                  {availableCoaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>
                      {coach.first_name} {coach.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium">Role</label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="head_coach">Head Coach</SelectItem>
                  <SelectItem value="assistant_coach">Assistant Coach</SelectItem>
                  <SelectItem value="team_parent">Team Parent</SelectItem>
                  <SelectItem value="board_member">Board Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleAddCoach} 
              disabled={!selectedCoachId || availableCoaches.length === 0 || isAdding}
            >
              {isAdding ? 'Adding...' : 'Add Coach'}
            </Button>
          </div>
          {availableCoaches.length === 0 && clubCoaches.length > 0 && (
            <p className="text-sm text-muted-foreground mt-2">
              All available club coaches have been assigned to this team.
            </p>
          )}
          {clubCoaches.length === 0 && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>No club coaches available.</strong> To add coaches to this team:
              </p>
              <ol className="text-sm text-yellow-700 mt-2 ml-4 list-decimal">
                <li>Go to the club management page</li>
                <li>Add people as coaches to the club first</li>
                <li>Then return here to assign them to this team</li>
              </ol>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Coaches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Team Coaches ({coaches.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {coaches.length === 0 ? (
            <div className="text-center py-8">
              <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Coaches Assigned</h3>
              <p className="text-muted-foreground">
                Add coaches to this team using the form above.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {coaches.map((coach) => (
                <div key={coach.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src="" alt={`${coach.profiles.first_name} ${coach.profiles.last_name}`} />
                      <AvatarFallback>
                        {coach.profiles.first_name[0]}{coach.profiles.last_name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h4 className="font-medium">
                        {coach.profiles.first_name} {coach.profiles.last_name}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {coach.profiles.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="min-w-[140px] justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleBadgeColor(coach.role)}>
                              {getRoleLabel(coach.role)}
                            </Badge>
                          </div>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[180px]">
                        <DropdownMenuItem 
                          onClick={() => updateCoachRole(coach.id, 'head_coach')}
                          className="flex items-center justify-between"
                        >
                          <span>Head Coach</span>
                          <Badge className="bg-blue-100 text-blue-800 ml-2">
                            Head Coach
                          </Badge>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => updateCoachRole(coach.id, 'assistant_coach')}
                          className="flex items-center justify-between"
                        >
                          <span>Assistant Coach</span>
                          <Badge className="bg-green-100 text-green-800 ml-2">
                            Assistant Coach
                          </Badge>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => updateCoachRole(coach.id, 'team_parent')}
                          className="flex items-center justify-between"
                        >
                          <span>Team Parent</span>
                          <Badge className="bg-purple-100 text-purple-800 ml-2">
                            Team Parent
                          </Badge>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => updateCoachRole(coach.id, 'board_member')}
                          className="flex items-center justify-between"
                        >
                          <span>Board Member</span>
                          <Badge className="bg-orange-100 text-orange-800 ml-2">
                            Board Member
                          </Badge>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeCoach(coach.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
